import React from 'react';
import { Close, ContentCopy, Send, Cancel, Schedule, CheckCircle, AccountBalance, Email, WhatsApp, Sms, Event, Person, Phone, Tag } from '@mui/icons-material';

const DAY_MS = 1000 * 60 * 60 * 24;

const toValidDate = (value) => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
    const d = toValidDate(value);
    if (!d) return '—';
    return d.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatCurrency = (amount, currency) => {
    try {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'CDF' }).format(amount || 0);
    } catch {
        return `${amount || 0} ${currency || ''}`.trim();
    }
};

const STATUS_INFO = {
    pending: { color: 'text-amber-700 bg-amber-50 border-amber-200', icon: Schedule, label: 'En attente de paiement' },
    paid: { color: 'text-blue-700 bg-blue-50 border-blue-200', icon: CheckCircle, label: 'Paiement effectué' },
    collected: { color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: AccountBalance, label: 'Fonds encaissés' },
    expired: { color: 'text-red-700 bg-red-50 border-red-200', icon: Cancel, label: 'Lien expiré' }
};

const NOTIFICATION_ICONS = { email: Email, whatsapp: WhatsApp, sms: Sms };

const InfoItem = ({ icon: Icon, label, children, mono = false }) => (
    <div className="flex items-start gap-3 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
            <Icon fontSize="small" />
        </div>
        <div className="min-w-0">
            <p className="text-xs font-medium text-slate-500">{label}</p>
            <p className={`text-sm font-semibold text-slate-900 break-words ${mono ? 'font-mono' : ''}`}>{children}</p>
        </div>
    </div>
);

const DateRow = ({ label, value }) => (
    <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
        <span className="text-slate-500">{label}</span>
        <span className="font-medium text-slate-900 text-right">{value}</span>
    </div>
);

export const PaymentLinkDetailsModal = ({ show, onClose, link, onCopyLink, onRenewLink, onResendLink, onCancelLink }) => {
    if (!show || !link) return null;

    const statusInfo = STATUS_INFO[link.status] || STATUS_INFO.pending;
    const StatusIcon = statusInfo.icon;
    const expiresAt = toValidDate(link.expiresAt);
    const paidAt = toValidDate(link.paidAt);
    const isPaid = link.status === 'paid' || link.status === 'collected';
    const isExpired = link.status === 'expired' || (expiresAt ? expiresAt < new Date() : false);
    const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / DAY_MS)) : null;
    const linkUrl = link.link || `${window.location.origin}/pay/${link.id}`;
    const notificationMethods = Array.isArray(link.notificationMethods) ? link.notificationMethods : [];
    const message = link.reason || link.message;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* En-tête */}
                <div className="flex items-start justify-between gap-4 p-6 border-b border-slate-100">
                    <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Lien de paiement</p>
                        <h3 className="text-xl font-bold text-slate-900 truncate">{link.title || 'Lien de paiement'}</h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors shrink-0" aria-label="Fermer">
                        <Close className="text-slate-500" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Montant et statut */}
                    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <p className="text-xs font-medium text-slate-500">Montant</p>
                            <p className="text-3xl font-bold text-slate-900">{formatCurrency(link.amount, link.currency)}</p>
                        </div>
                        <span className={`inline-flex items-center gap-2 self-start sm:self-auto px-3 py-1.5 rounded-full border text-sm font-semibold ${statusInfo.color}`}>
                            <StatusIcon fontSize="small" />
                            {statusInfo.label}
                        </span>
                    </div>

                    {/* Informations */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InfoItem icon={Person} label="Client">
                            {link.customer?.name || link.client || '—'}
                        </InfoItem>
                        <InfoItem icon={Phone} label="Contact">
                            {link.customer?.phoneNumber || link.customer?.email || link.clientContact || '—'}
                        </InfoItem>
                        <InfoItem icon={Tag} label="Réf." mono>
                            {link.transCode || link.code || '—'}
                        </InfoItem>
                        <InfoItem icon={Event} label={isPaid ? 'Payé le' : 'Expire le'}>
                            {isPaid ? formatDate(paidAt) : formatDate(expiresAt)}
                        </InfoItem>
                    </div>

                    {/* Délai d'expiration */}
                    {link.status === 'pending' && !isExpired && daysLeft !== null && (
                        <div className="flex items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4">
                            <Schedule className="text-amber-600" />
                            <div>
                                <p className="font-semibold text-amber-800">
                                    {daysLeft <= 1 ? 'Expire dans moins de 24 h' : `Valable encore ${daysLeft} jours`}
                                </p>
                                <p className="text-sm text-amber-700">Expire le {formatDate(expiresAt)}</p>
                            </div>
                        </div>
                    )}

                    {/* Message */}
                    {message && (
                        <div>
                            <p className="text-sm font-medium text-slate-600 mb-2">Message</p>
                            <p className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-slate-700">{message}</p>
                        </div>
                    )}

                    {/* Lien - sauf pour un paiement flash */}
                    {link.method !== 'flash' && (
                        <div>
                            <p className="text-sm font-medium text-slate-600 mb-2">Lien de paiement</p>
                            <div className="flex items-center gap-2">
                                <p className="flex-1 min-w-0 truncate rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 font-mono text-sm text-slate-700">
                                    {linkUrl}
                                </p>
                                {onCopyLink && (
                                    <button
                                        onClick={() => onCopyLink(link)}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors shrink-0"
                                    >
                                        <ContentCopy fontSize="small" />
                                        <span className="text-sm font-semibold">Copier</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Notifications */}
                    {notificationMethods.length > 0 && (
                        <div>
                            <p className="text-sm font-medium text-slate-600 mb-2">Envoyé par</p>
                            <div className="flex flex-wrap gap-2">
                                {notificationMethods.map((method) => {
                                    const IconComponent = NOTIFICATION_ICONS[method] || Email;
                                    return (
                                        <span key={method} className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200 text-sm font-medium text-blue-700 capitalize">
                                            <IconComponent fontSize="small" />
                                            {method}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Historique */}
                    <div className="rounded-2xl border border-slate-200 px-4 divide-y divide-slate-100">
                        <DateRow label="Créé le" value={formatDate(link.createdAt)} />
                        {!isPaid && <DateRow label="Expire le" value={formatDate(expiresAt)} />}
                        {paidAt && <DateRow label="Payé le" value={formatDate(paidAt)} />}
                        {link.paymentMethod && (
                            <DateRow label="Méthode de paiement" value={<span className="capitalize">{link.paymentMethod}</span>} />
                        )}
                    </div>

                    {/* Actions */}
                    {((link.status === 'pending' && (onResendLink || onCancelLink)) || (link.status === 'expired' && onRenewLink)) && (
                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            {link.status === 'pending' && onResendLink && (
                                <button
                                    onClick={() => onResendLink(link)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors"
                                >
                                    <Send fontSize="small" />
                                    <span className="text-sm font-semibold">Renvoyer</span>
                                </button>
                            )}
                            {link.status === 'pending' && onCancelLink && (
                                <button
                                    onClick={() => onCancelLink(link)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition-colors"
                                >
                                    <Cancel fontSize="small" />
                                    <span className="text-sm font-semibold">Annuler</span>
                                </button>
                            )}
                            {link.status === 'expired' && onRenewLink && (
                                <button
                                    onClick={() => onRenewLink(link)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
                                >
                                    <Schedule fontSize="small" />
                                    <span className="text-sm font-semibold">Renouveler le lien</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
